/**
 * @file game-connection-manager.ts
 * @description Connection manager for the Game Server WebSocket.
 *
 * Owns the full game connection lifecycle: session validation, WebSocket
 * transport, heartbeat, reconnection with exponential backoff, token refresh,
 * and close-code handling.  Exposes a minimal public API:
 *
 *   startSession(sessionId, wsUrl)  — validate → connect → heartbeat
 *   endSession()                    — intentional disconnect + cleanup
 *   sendAction(message)             — send or queue
 *   manualReconnect()               — user clicked "Reconnect"
 *
 * Connection state is pushed to `gameStore.connectionStatus`.
 */

import type { ClientMessage, ServerMessage } from '@danda/shared';
import { WsCloseCodeEnum, MAX_MESSAGE_QUEUE_SIZE } from '@danda/shared';
import { useGameStore, type GameConnectionStatus } from '@/lib/stores/gameStore';
import { usePlayerActivityStore } from '@/lib/stores/playerActivityStore';
import { auth } from '@/lib/firebase';
import { onIdTokenChanged, type Unsubscribe } from 'firebase/auth';
import { toast } from '@/lib/toast';
import { track } from '@/lib/analytics';
import { isSessionAlive } from '@/lib/session-liveness';
import { clearGameSessionState } from '@/lib/clear-game-session-state';
import { classifyCloseCode } from '@/lib/ws-close-classifier';
import { emitSessionExpired } from '@/lib/session-expired-events';
import { z } from 'zod';

// =============================================================================
// Diagnostics — see #12 in docs/playtest/2026-05-05-run-bugs.md.
// =============================================================================

/**
 * Backend serde error: `Failed to parse client message error=missing field item_type`.
 * The `UsableItem` enum is internally-tagged on `item_type` (Rust `#[serde(tag = "item_type")]`).
 * Any `use_item` intent whose nested `item` is missing the discriminator gets dropped
 * silently. We audit outbound payloads here and log the full JSON so the next
 * playtest captures the exact malformed shape — the FE construction path producing
 * this is unknown (all 3 known builders set item_type explicitly), so we need
 * field evidence. Remove once the source path is identified and fixed.
 */
function auditUseItemPayload(action: unknown): void {
  if (typeof action !== 'object' || action === null) return;
  const a = action as Record<string, unknown>;
  // ClientMessage envelopes vary; the Intent itself may be at action.intent
  // (e.g. PlayerIntent wire message) or nested deeper. Walk both shapes.
  const candidate =
    (a.intent as Record<string, unknown> | undefined) ?? (a as Record<string, unknown>);
  if (
    candidate &&
    candidate.intent_type === 'use_item' &&
    typeof candidate.item === 'object' &&
    candidate.item !== null &&
    !('item_type' in (candidate.item as Record<string, unknown>))
  ) {
    console.warn(
      '[#12 audit] Outbound use_item intent missing item.item_type — backend will reject. Full payload:',
      JSON.stringify(action, null, 2)
    );
  }
}

// =============================================================================
// Config
// =============================================================================

interface GameConnectionConfig {
  reconnectAttempts?: number;
  reconnectDelay?: number;
  heartbeatInterval?: number;
  /**
   * How long to wait for a pong before declaring the WS dead and
   * force-reconnecting. MUST be wider than the longest plausible round
   * narration: Pongs ride the per-session outbound mpsc channel and sit
   * behind narrative stream chunks during long rounds (30-90s observed).
   * Anything ≤60s misfires mid-round and silently disconnects healthy clients.
   */
  pongTimeoutMs?: number;
}

const DEFAULT_CONFIG = {
  reconnectAttempts: 5,
  reconnectDelay: 2000,
  heartbeatInterval: 30000,
  pongTimeoutMs: 180000,
} as const;

// =============================================================================
// Schema validation (carried over from game-websocket-service.ts)
// =============================================================================

const stringOrNumber = z.union([z.string(), z.number()]);

const inGameTimeSchema = z.object({
  formatted: z.string(),
  day: z.number(),
  hour: z.number(),
  minute: z.number(),
  timeOfDay: z.string(),
});

const serverEventSchema = z.object({ type: z.string() }).loose();

const serverMessageSchemas: Partial<Record<ServerMessage['type'], z.ZodTypeAny>> = {
  combat_highlight_ready: z
    .object({
      type: z.literal('combat_highlight_ready'),
      id: z.string(),
      sender: z.literal('polymyth'),
      round: z.number(),
      scene_id: z.string(),
      char_id: z.string(),
      video_url: z.url(),
      duration_seconds: z.number(),
      direction: z.enum(['player_attacks', 'player_is_hit']),
      action_description: z.string(),
      cost_pm: z.number(),
    })
    .loose(),
  missed_rounds: z
    .object({
      type: z.literal('missed_rounds'),
      rounds: z.array(
        z.object({
          round: z.number(),
          narrative: z.string(),
          timestamp: z.string(),
        })
      ),
      disconnected_at_round: z.number(),
      current_round: z.number(),
    })
    .loose(),
  action_queued: z
    .object({
      type: z.literal('action_queued'),
      characterId: z.string(),
      action: z.string(),
    })
    .loose(),
  game_state: z
    .object({
      type: z.literal('game_state'),
      state: z
        .object({
          sessionId: z.string(),
          currentRound: z.number(),
          status: z.string(),
          characters: z.array(
            z
              .object({
                charId: z.string(),
                name: z.string(),
                hp: z.number(),
                maxHp: z.number(),
                conditions: z.array(z.union([z.string(), z.object({ name: z.string() }).loose()])),
                initiative: z.number().optional(),
              })
              .loose()
          ),
          scene: z
            .object({
              sceneId: z.string(),
              name: z.string(),
              description: z.string().optional(),
              imageUrl: z.string().optional(),
            })
            .loose(),
          inGameTime: inGameTimeSchema,
          playerCharacterId: z.string().optional(),
          currentTurn: z.string().optional(),
          moduleTitle: z.string().optional(),
          moduleHeroImage: z.string().optional(),
          partyName: z.string().optional(),
          activeBoons: z
            .array(z.object({ name: z.string(), effects: z.array(z.string()) }).loose())
            .optional(),
        })
        .loose(),
    })
    .loose(),
  error: z
    .object({
      type: z.literal('error'),
      message: z.string(),
      code: z.string().optional(),
    })
    .loose(),
  chat_broadcast: z
    .object({
      type: z.literal('chat_broadcast'),
      char_id: z.string(),
      char_name: z.string(),
      message: z.string(),
    })
    .loose(),
  player_ready: z
    .object({
      type: z.literal('player_ready'),
      userId: z.string(),
      characterId: z.string().optional(),
      characterName: z.string().optional(),
      ready: z.boolean(),
      timestamp: z.string(),
    })
    .loose(),
  all_players_ready: z
    .object({
      type: z.literal('all_players_ready'),
      playerCount: z.number(),
      timestamp: z.string(),
    })
    .loose(),
  player_disconnected: z
    .object({
      type: z.literal('player_disconnected'),
      userId: z.string(),
      characterId: z.string().optional(),
      characterName: z.string().optional(),
      gracePeriodMs: z.number(),
      sent_at: z.string().optional(),
    })
    .loose(),
  player_reconnected: z
    .object({
      type: z.literal('player_reconnected'),
      userId: z.string(),
      characterId: z.string().optional(),
      characterName: z.string().optional(),
      sent_at: z.string().optional(),
    })
    .loose(),
  scene_transition: z
    .object({
      type: z.literal('scene_transition'),
      fromScene: z.string(),
      toScene: z.object({
        sceneId: z.string(),
        name: z.string(),
        description: z.string().optional(),
        imageUrl: z.string().optional(),
      }),
      inGameTime: inGameTimeSchema,
      timestamp: z.string(),
    })
    .loose(),
  scene_image_generated: z
    .object({
      type: z.literal('scene_image_generated'),
      scene_name: z.string(),
      image_url: z.string(),
      image_id: z.string(),
      duration_ms: z.number(),
    })
    .loose(),
  session_ended: z
    .object({
      type: z.literal('session_ended'),
      reason: z.string(),
      finalRound: z.number(),
      summary: z.string().optional(),
      partyId: z.string().optional(),
      moduleId: z.string().optional(),
      finalGameTimeSeconds: z.number().optional(),
      // Optional for wire-compat during a partial deploy where backend
      // hasn't shipped the field yet. Once both sides are deployed the
      // server sets this explicitly; the FE's `event.isTrainingDuel === true`
      // check treats missing-or-false identically. Schema runs shape
      // validation only — the raw event is forwarded to handleGameEvent,
      // not the parsed result, so any default would not actually apply.
      isTrainingDuel: z.boolean().optional(),
      timestamp: stringOrNumber,
    })
    .loose(),
  session_ending: z
    .object({
      type: z.literal('session_ending'),
      reason: z.string(),
      // See `session_ended.isTrainingDuel` — shape-validation only.
      isTrainingDuel: z.boolean().optional(),
    })
    .loose(),
  round_result: z
    .object({
      type: z.literal('round_result'),
      round: z.number(),
      narrative: z.string(),
      changes: z.array(
        z.object({
          char_id: z.string(),
          change_type: z.string(),
          value: z.unknown(),
        })
      ),
      in_combat: z.boolean(),
      character_actions: z
        .array(
          z.object({
            char_id: z.string(),
            char_name: z.string(),
            action_type: z.string(),
            description: z.string(),
            target: z.string().optional(),
            success: z.boolean().optional(),
            damage: z.number().optional(),
            healing: z.number().optional(),
            avatar_url: z.string().optional(),
            avatar_emoji: z.string().optional(),
            portrait_url: z.string().optional(),
          })
        )
        .optional(),
      session_changes: z
        .object({
          encounterType: z.string().optional(),
          combatTrigger: z.string().optional(),
          combatResolution: z.string().optional(),
          restCompleted: z.string().optional(),
        })
        .optional(),
      in_game_time: inGameTimeSchema.optional(),
      observable_party_members: z.array(z.object({}).loose()).optional(),
      observable_non_party_members: z.array(z.object({}).loose()).optional(),
      my_char_narrative: z.string().optional(),
      my_private_messages: z
        .array(
          z.object({
            from_char_id: z.string(),
            from_char_name: z.string(),
            message: z.string(),
            avatar_url: z.string().optional(),
            avatar_emoji: z.string().optional(),
            portrait_url: z.string().optional(),
          })
        )
        .optional(),
      my_rejected_intents: z
        .array(
          z.object({
            originalIntent: z.string(),
            intentKind: z.string().optional(),
            objectName: z.string().optional(),
            subjectName: z.string().optional(),
            playerReason: z.string().optional(),
            devReason: z.string(),
          })
        )
        .optional(),
      my_character_permanent: z.object({}).loose().optional(),
      my_character_session: z.object({}).loose().optional(),
    })
    .loose(),
  map_update: z
    .object({
      type: z.literal('map_update'),
      currentLocationId: z.string(),
      currentSceneId: z.string(),
      mapImage: z.string().optional(),
      discoveredLocations: z.array(z.string()),
      discoveredScenes: z.array(z.string()),
      timestamp: z.string(),
    })
    .loose(),
  movement_proposed: z
    .object({
      type: z.literal('movement_proposed'),
      proposalId: z.string(),
      proposerId: z.string(),
      targetName: z.string(),
      isTravel: z.boolean(),
      travelTime: z.string().optional(),
      voters: z.array(z.string()),
      timeoutMs: z.number(),
      timestamp: z.string(),
    })
    .loose(),
  movement_vote_cast: z
    .object({
      type: z.literal('movement_vote_cast'),
      proposalId: z.string(),
      voterId: z.string(),
      vote: z.string(),
      yesCount: z.number(),
      noCount: z.number(),
      votesNeeded: z.number(),
      timestamp: z.string(),
    })
    .loose(),
  movement_started: z
    .object({
      type: z.literal('movement_started'),
      targetSceneId: z.string(),
      targetLocationId: z.string().optional(),
      targetName: z.string(),
      travelMinutes: z.number(),
      isTravel: z.boolean(),
      timestamp: z.string(),
    })
    .loose(),
  movement_complete: z
    .object({
      type: z.literal('movement_complete'),
      sceneId: z.string(),
      sceneName: z.string(),
      locationId: z.string(),
      description: z.string(),
      inGameTime: inGameTimeSchema,
      timestamp: z.string(),
    })
    .loose(),
  movement_blocked: z
    .object({
      type: z.literal('movement_blocked'),
      reason: z.string(),
      hint: z.string().optional(),
      exitId: z.string(),
      timestamp: z.string(),
    })
    .loose(),
  round_failed: z
    .object({
      type: z.literal('round_failed'),
      round: z.number(),
      message: z.string(),
    })
    .loose(),
};

// =============================================================================
// GameConnectionManager
// =============================================================================

class GameConnectionManager {
  private ws: WebSocket | null = null;
  private config: Required<GameConnectionConfig>;

  // Connection identity
  private lastUrl: string | null = null;
  private lastSessionId: string | null = null;
  private lastHttpUrl: string | null = null;
  private pendingConnect: Promise<void> | null = null;

  // Close code disambiguation (C2)
  private intentionalClose = false;

  // Terminal state — blocks reconnection after fatal close codes
  private sessionTerminated = false;

  // Teardown epoch — bumped on every endSession(). doConnect captures it before
  // its `await getAuthToken()` and bails if it changed during the await, so a
  // fire-and-forget reopen (the 4409 silent reclaim) can't resurrect an orphan
  // socket on a session that endSession() tore down mid-await. The timer-based
  // scheduleReconnect is cancellable via clearReconnectTimer(); an in-flight
  // promise is not, so this is its equivalent guard.
  private connectionEpoch = 0;

  // Reconnection
  private reconnectCount = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  // Backoff credit is earned, not granted on open. A socket must stay open
  // for STABLE_CONNECTION_MS before reconnectCount resets, resetting in
  // onopen let a connect-then-drop cycle (Cloud Run instance churn, a
  // backend panic on one message, half-open sockets) reconnect every 2s
  // forever without ever reaching the attempt cap, replaying missed-round
  // history into the store on every cycle.
  private stabilityTimer: ReturnType<typeof setTimeout> | null = null;
  private static readonly STABLE_CONNECTION_MS = 30_000;
  // Set when max auto-reconnect retries are exhausted.
  // Blocks ALL automatic reconnections — only manual reconnect clears this.
  private autoReconnectDisabled = false;
  // Rapid-cycle handshake-failure detection: WS opens then closes before any
  // message arrives (server cold-start saturation, half-open edge, post-deploy
  // churn, /play first-load racing the grace-period cleanup). After
  // HANDSHAKE_FAILURE_LIMIT consecutive failures we trip status=disconnected
  // so the existing DisconnectedOverlay surfaces a manual retry.
  private handshakeFailures = 0;
  private firstMsgAfterOpen = false;
  private static readonly HANDSHAKE_FAILURE_LIMIT = 3;
  // Timestamp of the last successful WS open, used to distinguish a transient
  // self-supersede (React StrictMode's dev double-mount opens a second socket
  // moments after the first connects; the server's "newest wins" sends 4409
  // to the still-current socket) from a genuine cross-tab/device takeover. A
  // 4409 within SUPERSEDE_RECLAIM_WINDOW_MS of connecting is reclaimed SILENTLY
  // (reconnect underneath without flipping the UI off the game); a later one
  // stays terminal. `silentSupersedeReclaimUsed` bounds it to one reclaim per
  // connect-epoch so a genuine two-tab fight can't ping-pong forever — the
  // second supersede falls through to the terminal overlay. Reset only on a
  // fresh session / manual reconnect (NOT on the reclaim's own reopen).
  private lastConnectedAt: number | null = null;
  private silentSupersedeReclaimUsed = false;
  private static readonly SUPERSEDE_RECLAIM_WINDOW_MS = 5000;

  // Message queue (offline resilience)
  private messageQueue: ClientMessage[] = [];

  // Token refresh
  private currentToken: string | null = null;
  private tokenUnsubscribe: Unsubscribe | null = null;

  constructor(config: GameConnectionConfig = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // ===========================================================================
  // Public API
  // ===========================================================================

  /**
   * Start a game session: validate → connect → heartbeat.
   * Called by GameLayout when sessionId + wsUrl are available.
   */
  async startSession(
    sessionId: string,
    wsUrl: string,
    httpUrl: string,
    options?: { skipValidation?: boolean }
  ): Promise<void> {
    // Clear termination guard only for genuine session switches (G18).
    // lastSessionId being null after a 4004 is NOT a session switch — keep terminated.
    if (this.lastSessionId && this.lastSessionId !== sessionId) {
      this.sessionTerminated = false;
      this.silentSupersedeReclaimUsed = false;
      // Reset the game store so the ReconnectScreen gate works on the new session.
      // Without this, `gameInitialized` stays `true` from the previous session
      // (it's in-memory and not cleared on normal session end), so the full
      // game UI renders instantly with stale characters/messages, defeating
      // the backend warmup buffer that holds game_state for ~40s.
      // Reset clears gameInitialized → false, messages, characters, and all
      // session-scoped state. The next `game_state` (when the warmup gate
      // releases) rebuilds everything from the server's authoritative view.
      const previousSessionId = this.lastSessionId;
      useGameStore.getState().reset();
      // Make sure `game-last-session-id` reflects the previous session before
      // the upcoming `game_state` arrives. `reset()` is a pure in-memory
      // reset and does NOT touch localStorage, so the value is usually
      // already correct — but if the prior session ended via a path that
      // ran `clearGameSessionState('all')` (e.g. /end-session, kicked,
      // SIGNED_OUT_ELSEWHERE), localStorage was wiped while
      // `this.lastSessionId` (a GCM class field) still holds the old id.
      // Restoring it ensures the gameStore's `game_state` handler sees a
      // non-null `lastSessionId` to compare against the incoming sessionId and
      // resolves `isSessionSwitch = true`, so stale messages are cleared. (The
      // warmup-replay gate itself is flagged by `warmup_holding`, which is sent
      // only for genuinely warming-up sessions and never for resumes, and is
      // armed by the drained `game_state` consuming that flag.)
      if (typeof localStorage !== 'undefined') {
        localStorage.setItem('game-last-session-id', previousSessionId);
      }
    }

    if (this.sessionTerminated) {
      if (useGameStore.getState().connectionStatus.status !== 'ended') {
        this.setStatus({ status: 'ended', reason: 'dm_ended' });
      }
      return Promise.reject(new Error('Session was terminated by server'));
    }

    // Debounce: reuse pending connection to same session
    if (this.pendingConnect && this.lastUrl === wsUrl && this.lastSessionId === sessionId) {
      return this.pendingConnect;
    }

    // Cancel any existing connection if switching sessions
    if (this.pendingConnect) {
      console.warn('[GameCM] Cancelling pending connection to start new one');
      this.endSession();
    }

    // Already connected to the correct session
    if (this.ws?.readyState === WebSocket.OPEN) {
      if (this.lastUrl === wsUrl && this.lastSessionId === sessionId) {
        return;
      }
      this.endSession();
    }

    this.lastUrl = wsUrl;
    this.lastSessionId = sessionId;
    this.lastHttpUrl = httpUrl;

    // The finally must only clear ITS OWN attempt: endSession() nulls
    // pendingConnect mid-flight, and a follow-up startSession may have
    // installed a fresh attempt by the time this stale one settles.
    const attempt = this._startSession(sessionId, wsUrl, httpUrl, options?.skipValidation).finally(
      () => {
        if (this.pendingConnect === attempt) {
          this.pendingConnect = null;
        }
      }
    );
    this.pendingConnect = attempt;

    return attempt;
  }

  /**
   * End the current session. Intentional disconnect — no reconnection.
   */
  endSession(): void {
    // Invalidate any in-flight doConnect() that's awaiting its auth token —
    // its post-await epoch check will see the bump and abort instead of
    // opening an orphan socket on this now-torn-down session.
    this.connectionEpoch++;
    // Drop the doomed attempt from the debounce. The epoch bump makes the
    // in-flight _startSession resolve WITHOUT connecting, so handing it to a
    // follow-up startSession (same session id, e.g. a GameLayout remount)
    // would strand that caller on 'idle' with no socket, no rejection, and
    // no watchdog (mirrors websocket-service.disconnect()).
    this.pendingConnect = null;
    this.stopTokenListener();
    this.clearReconnectTimer();

    if (this.ws) {
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onmessage = null;
      this.ws.close(WsCloseCodeEnum.NORMAL_CLOSURE, 'Client disconnect');
      this.ws = null;
    }

    this.reconnectCount = 0;
    this.autoReconnectDisabled = false;
    this.handshakeFailures = 0;
    this.firstMsgAfterOpen = false;
    this.messageQueue = [];
    // Preserve lastUrl/lastSessionId/lastHttpUrl — manualReconnect() needs them.
    // They get overwritten on next startSession() call anyway.

    // Clear recap timer to prevent stale status changes after navigation
    const { _recapTimerId } = useGameStore.getState();
    if (_recapTimerId !== null) {
      clearTimeout(_recapTimerId);
      useGameStore.setState({ _recapTimerId: null });
    }

    // endSession() is a client-initiated cleanup — by definition the session
    // is over from THIS singleton's perspective, so future startSession() calls
    // must NOT be blocked by stale `sessionTerminated`. Without this reset, a
    // 4100/auth-recovery that flipped the flag stays sticky across the
    // GameLayout unmount, and the next /play mount for a NEW session takes the
    // terminal-guard reject path with no UI recovery. The flag should only
    // persist while the WS is still notionally bound to a terminated session.
    //
    // Race-with-token-listener safety: the in-flight `onIdTokenChanged`
    // callback (see `startTokenListener`) reads `sessionTerminated` AFTER
    // its `await user.getIdToken()`, so if endSession() runs during that
    // await the post-await guard sees `false` and skips the auto-reconnect.
    // Plus we just nulled `this.ws` above (line 503), so the OPEN-socket
    // branch in the listener is also skipped. No reconnect leaks out.
    this.sessionTerminated = false;

    // Always return to idle. Persisting 'ended' across endSession was
    // tempting (it suppressed the noisy "startSession rejected: Session
    // was terminated by server" log on a remount race) — but GameLayout's
    // status-watch effect fires whenever `connectionStatus` is 'ended',
    // including on a fresh /play mount for a NEW session. Holding 'ended'
    // there causes navigate('/lobby') → ProtectedRoute redirects /play →
    // remount → 'ended' again → loop. Reset to 'idle'; the rejected-log
    // noise is harmless.
    useGameStore.getState().setConnectionStatus({ status: 'idle' });
    useGameStore.getState().setGameSessionId(null);
    // Clear the duel-recap markers. They're scoped to the just-ended
    // session, but the standard `/end-session` slash command flow is the
    // only path that goes through `clearGameSessionState('all')` →
    // `reset()` to wipe them. Non-standard exits (browser back, GameLayout
    // unmount on navigate, anything that bypasses the ChatPanel intercept)
    // would otherwise leave both flags true into the next session, and the
    // `isDuelRecapWindow` check on the next non-duel 4100 close
    // (line ~964 below) would wrongly hold the dead WS UI instead of
    // returning the player to the lobby.
    //
    // Same connection-scoping for the warmup-replay gate: a back-out during
    // warmup (after `warmup_holding`, before the drain) leaves `warmupActive`
    // set with no game_state to consume it; a later same-session re-entry
    // skips `reset()` (lastSessionId matches), and the stale flag would arm
    // the gate from that re-entry's game_state, swallowing the resume
    // kickstart round (#35 class). The socket-open clear in `connect()`
    // covers reconnects; this covers the session-teardown path.
    useGameStore.setState({
      sessionEndIsDuel: false,
      sessionEndSummary: null,
      warmupActive: false,
      sessionStartRound: null,
    });
  }

  /**
   * Manual reconnect triggered by user clicking "Reconnect" on overlay.
   * Resets counter and tries one fresh connect cycle (C6).
   */
  async manualReconnect(): Promise<void> {
    const url = this.lastUrl;
    const sessionId = this.lastSessionId;
    const httpUrl = this.lastHttpUrl;
    if (!url || !sessionId || !httpUrl) {
      throw new Error('No previous session to reconnect to');
    }
    this.clearReconnectTimer();
    this.sessionTerminated = false;
    this.autoReconnectDisabled = false;
    this.reconnectCount = 0;
    this.handshakeFailures = 0;
    this.firstMsgAfterOpen = false;
    this.silentSupersedeReclaimUsed = false;
    this.setStatus({ status: 'connecting' });
    return this.startSession(sessionId, url, httpUrl);
  }

  /**
   * Send a game action (move, attack, chat, etc).
   * Queues if not connected.
   */
  sendAction(action: ClientMessage): void {
    // Defensive payload audit — diagnostics for #12 (Failed to parse client
    // message error=missing field item_type). The Rust UsableItem enum is
    // serde-tagged on `item_type`, so any use_item intent that omits the
    // discriminator is silently dropped backend-side. Log the full JSON so
    // the next playtest captures the exact malformed shape. Remove once
    // the source code path is identified and fixed.
    auditUseItemPayload(action);

    usePlayerActivityStore.getState().markPlayerIntent();

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(action));
    } else {
      this.messageQueue.push(action);
      if (this.messageQueue.length > MAX_MESSAGE_QUEUE_SIZE) {
        this.messageQueue.splice(0, this.messageQueue.length - MAX_MESSAGE_QUEUE_SIZE);
      }
    }
  }

  // ===========================================================================
  // Private — session lifecycle
  // ===========================================================================

  private async _startSession(
    sessionId: string,
    wsUrl: string,
    httpUrl: string,
    skipValidation?: boolean
  ): Promise<void> {
    // Teardown guard for the validation window. doConnect() has its own epoch
    // capture for the auth-token await, but it captures AFTER endSession()'s
    // bump: an endSession() landing during the validateSession() fetch below
    // (user navigates off /play while the REST round-trip is in flight) would
    // otherwise sail through both checks and open an orphan socket whose
    // onopen flips gameStore back to 'connected' with no mounted owner.
    const epochAtStart = this.connectionEpoch;

    // Phase 1: Validate session exists (G1-G4)
    // Skip for fresh sessions from lobby — the lobby just created the session, no need to round-trip.
    if (skipValidation) {
      console.debug('[GameCM] Skipping session validation (fresh from lobby)');
    } else {
      this.setStatus({ status: 'validating' });
      const exists = await this.validateSession(sessionId, httpUrl, epochAtStart);
      if (this.connectionEpoch !== epochAtStart || this.sessionTerminated) {
        // Checked before `exists`: a torn-down session must not proceed even
        // when validation says it exists, and bailing here also avoids
        // clobbering the 'idle' status endSession() just set.
        console.debug('[GameCM] _startSession aborted: session torn down during validation');
        return;
      }
      if (!exists) {
        // validateSession already handled redirect
        return;
      }
    }

    // Phase 2: Connect WebSocket
    this.setStatus({ status: 'connecting' });
    await this.doConnect(wsUrl, sessionId);

    // doConnect resolves silently on a teardown-epoch abort; the token
    // listener must not restart after endSession() stopped it.
    if (this.connectionEpoch !== epochAtStart || this.sessionTerminated) {
      return;
    }

    // Phase 3: Start token listener for proactive refresh (G16)
    this.startTokenListener();
  }

  /**
   * REST check: does this session exist on the game server? (G1-G4)
   * Returns true if we should proceed to WS connect.
   *
   * `epochAtStart` guards the session-gone branches: they mutate global state
   * (clearGameSessionState, 'ended' status, toast), and a stale response
   * landing after endSession() must not fire them. The caller's own post-await
   * check can't help because these side effects run BEFORE we return. A stale
   * 'ended' status is the worst of them: GameLayout's ended early-bail would
   * skip the WS bring-up on the next legitimate /play mount.
   */
  private async validateSession(
    sessionId: string,
    httpUrl: string,
    epochAtStart: number
  ): Promise<boolean> {
    // Validate format (G4)
    const sessionIdRegex = /^[a-zA-Z0-9_-]+_[a-f0-9]{8}$/;
    if (!sessionIdRegex.test(sessionId)) {
      console.error('[GameCM] Invalid session ID format:', sessionId);
      clearGameSessionState('all');
      return false; // caller will not proceed
    }

    try {
      const response = await fetch(`${httpUrl}/api/sessions/${sessionId}/exists`);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const data = await response.json();
      if (data.exists) return true;
      if (this.connectionEpoch !== epochAtStart) return false;

      // Session confirmed gone (G2)
      console.warn('[GameCM] Session does not exist:', sessionId);
      clearGameSessionState('all');
      this.setStatus({ status: 'ended', reason: 'error' });
      toast.info('Session Ended', { description: 'This game session has ended.' });
      return false;
    } catch (error) {
      // Retry once after 2s (G3)
      console.warn('[GameCM] Session check failed, retrying...', error);
      await new Promise((r) => setTimeout(r, 2000));

      try {
        const retry = await fetch(`${httpUrl}/api/sessions/${sessionId}/exists`);
        if (!retry.ok) throw new Error(`HTTP ${retry.status}`);
        const retryData = await retry.json();
        if (retryData.exists) return true;
        if (this.connectionEpoch !== epochAtStart) return false;

        // Session confirmed gone after retry
        console.warn('[GameCM] Session does not exist after retry:', sessionId);
        clearGameSessionState('all');
        this.setStatus({ status: 'ended', reason: 'error' });
        toast.info('Session Ended', { description: 'This game session has ended.' });
        return false;
      } catch {
        // Server still down — proceed to WS anyway, it will handle errors (G3)
        console.error('[GameCM] Session check failed after retry — proceeding anyway');
        return true;
      }
    }
  }

  // ===========================================================================
  // Private — WebSocket connection
  // ===========================================================================

  private async doConnect(url: string, sessionId: string): Promise<void> {
    this.clearReconnectTimer();
    this.intentionalClose = false;

    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onmessage = null;
      this.ws.close(WsCloseCodeEnum.NORMAL_CLOSURE, 'Reconnecting');
      this.ws = null;
    }

    // Capture the teardown epoch AFTER our own ws-cleanup above (which doesn't
    // bump it) and BEFORE the async token fetch. If endSession() runs during
    // the await — e.g. GameLayout unmounts (navigate-away, or the StrictMode
    // cleanup the 4409 reclaim races) — the epoch changes and we abort below
    // rather than opening a socket on a dead session whose onopen would flip
    // status back to 'connected'. `sessionTerminated` covers the terminal-close
    // paths (4003/4004/4100/auth) that set it without bumping the epoch.
    const epochAtConnect = this.connectionEpoch;
    const token = await this.getAuthToken();
    if (this.connectionEpoch !== epochAtConnect || this.sessionTerminated) {
      // Resolve silently (mirror the _startSession validation-window abort).
      // Throwing here rejects into stale closures that outlive teardown:
      // GameLayout's startSession catch reads the CURRENT status, which by
      // now belongs to a remount's fresh attempt, and navigates the live
      // connect to /lobby with "Session unavailable". No socket exists yet,
      // so there is nothing to clean up.
      console.debug('[GameCM] doConnect aborted: session torn down during auth token fetch');
      return;
    }

    return new Promise((resolve, reject) => {
      try {
        // Build URL
        let wsUrl: URL;
        if (url.includes(`/ws/${sessionId}`)) {
          wsUrl = new URL(url);
        } else {
          wsUrl = new URL(`${url}/${sessionId}`);
        }

        // The inner Promise MUST be settled exactly once. If the WS opens →
        // resolve. If it closes or errors before opening → reject. Without
        // this guard, a "close before open" path (Cloud Run cold start, server
        // overload, network blip) leaves the Promise pending forever, which
        // pins `pendingConnect` in `startSession()` and turns every subsequent
        // reconnect attempt into a no-op (returns the dead Promise). The user
        // sees "Opening a portal..." indefinitely with no escape.
        let settled = false;
        const settle = (fn: () => void) => {
          if (settled) return;
          settled = true;
          fn();
        };

        // `warmupActive` is connection-scoped: `warmup_holding` arrives per
        // socket (the backend re-sends it on every connect while the warmup
        // buffer is active), so a fresh socket starts with a clean flag and
        // re-arms only if THIS connection's warmup_holding arrives. Without
        // this, a socket that died between warmup_holding and the drained
        // game_state leaks the stale flag into a post-release reconnect (or a
        // later same-session resume), arming the warmup-replay gate from a
        // plain reconnect game_state and swallowing the next live round's
        // result (the #35 stuck-thinking class).
        useGameStore.setState({ warmupActive: false });

        // Auth via subprotocol (not query params)
        const protocols = token ? ['bearer', token] : undefined;
        const newWs = protocols
          ? new WebSocket(wsUrl.toString(), protocols)
          : new WebSocket(wsUrl.toString());
        this.ws = newWs;

        // Superseded-connection guard: capture `newWs` in each handler's
        // closure and skip if `this.ws` has since been replaced by a newer
        // socket. Without this, a 4411 close on the OLD socket (server-side
        // single-frontend kick during a fast same-session reconnect race)
        // would run handleClose unconditionally — which hard-navigates to
        // /dashboard via window.location.href — even though the NEW socket
        // is the one the user actually has open. Mirrors the lobby pattern
        // in `websocket-service.ts`.
        newWs.onopen = () => {
          if (this.ws !== newWs) {
            console.log('[GameCM] Connection opened but superseded — closing');
            newWs.close();
            return;
          }
          const wasReconnecting = this.reconnectCount > 0;
          this.firstMsgAfterOpen = false;
          this.lastConnectedAt = Date.now();
          // Reset the attempt counter only once THIS socket proves stable
          // (see stabilityTimer field comment). The callback re-checks the
          // socket is still current and open, so a stale fire is a no-op.
          this.clearStabilityTimer();
          this.stabilityTimer = setTimeout(() => {
            this.stabilityTimer = null;
            if (this.ws === newWs && newWs.readyState === WebSocket.OPEN) {
              this.reconnectCount = 0;
              this.handshakeFailures = 0;
            }
          }, GameConnectionManager.STABLE_CONNECTION_MS);
          this.setStatus({ status: 'connected' });
          useGameStore.getState().setGameSessionId(sessionId);
          this.flushMessageQueue();
          if (wasReconnecting) {
            toast.success('Reconnected to Game', {
              description: 'Connection to game session restored.',
            });
          }
          settle(resolve);
        };

        newWs.onclose = (event) => {
          if (this.ws !== newWs) {
            console.log('[GameCM] Ignoring close for superseded connection');
            return;
          }
          this.clearStabilityTimer();
          // Settle the inner Promise so the `pendingConnect` debounce in
          // startSession() releases before any scheduled reconnect runs.
          settle(() => reject(new Error(`Game WS closed before open: code=${event.code}`)));
          this.handleClose(event, reject);
        };

        newWs.onerror = (error) => {
          if (this.ws !== newWs) {
            return;
          }
          console.error('[GameCM] WebSocket error:', error);
          track('app_error', {
            message: 'WebSocket connection error',
            name: 'ws_error',
            context: 'game_ws',
          });
          settle(() => reject(error));
          // Let onclose handle reconnection
        };

        newWs.onmessage = (event) => {
          if (this.ws !== newWs) {
            return;
          }
          this.handleMessage(event.data);
        };
      } catch (error) {
        reject(error);
      }
    });
  }

  // ===========================================================================
  // Private — close code handling (all in one place)
  // ===========================================================================

  private handleClose(event: CloseEvent, reject: (reason: Error) => void): void {
    const code = event.code;

    // Intentional close from client (1000 + flag) — promise was resolved in
    // onopen; nothing further to do.
    if (code === WsCloseCodeEnum.NORMAL_CLOSURE && this.intentionalClose) {
      this.intentionalClose = false;
      return;
    }

    // CONNECTION_SUPERSEDED (4409): this socket was displaced by a NEWER
    // connection for the same user + session. The `this.ws !== newWs` guard in
    // onclose swallows the OLDER socket of a self-supersede pair, but NOT the
    // currently-connected socket being displaced — which is exactly what dev
    // StrictMode produces (see the reclaim branch below). So a 4409 here is
    // either that transient self-supersede or a genuine cross-tab/device
    // takeover. The reclaim branch handles the former; the terminal branch
    // tears down cleanly for the latter (avoid a dead-but-connected state with
    // a stale `this.ws`, live token listener, and queued sends), without
    // hard-redirecting to /dashboard (4411's job) and without auto-reconnecting
    // (that would fight the newer connection — "newest wins").
    if (code === WsCloseCodeEnum.CONNECTION_SUPERSEDED) {
      this.intentionalClose = false;

      // Transient self-supersede: in dev, React StrictMode double-mounts
      // GameLayout, opening a second socket moments after the first connects.
      // The server's "newest wins" then sends 4409 to the first socket, which
      // is still `this.ws` here (the `this.ws !== newWs` guard in onclose only
      // catches the OLDER socket of a pair, not the connected one being
      // displaced). Without this gate, every dev game-start showed the
      // sequence lobby → loader → game → loader → game: the terminal overlay
      // (then, with an earlier gating, the "Reconnecting…" screen) bounced the
      // UI off the rendered game and back. Reclaim SILENTLY instead: reopen the
      // socket via `doConnect` WITHOUT a status change, so the game UI stays
      // mounted (status stays 'connected') while the wire reconnects
      // underneath — no second loader. Gated to a supersede within the
      // initial-connect window, used at most once per connect-epoch
      // (`silentSupersedeReclaimUsed`), so a genuine two-tab fight can't
      // ping-pong: the second supersede falls through to the terminal overlay.
      const recentlyConnected =
        this.lastConnectedAt !== null &&
        Date.now() - this.lastConnectedAt < GameConnectionManager.SUPERSEDE_RECLAIM_WINDOW_MS;
      if (
        recentlyConnected &&
        !this.silentSupersedeReclaimUsed &&
        this.reconnectCount === 0 &&
        !this.sessionTerminated &&
        this.lastUrl !== null &&
        this.lastSessionId !== null
      ) {
        this.silentSupersedeReclaimUsed = true;
        // Do NOT clear messageQueue here: this is a transient reclaim, not a
        // terminal close. Anything queued by sendAction during the supersede
        // window (the player can't tell the wire is down, the UI stays
        // 'connected') flushes in the reclaimed socket's onopen. Clearing it
        // ghost-dropped chat messages that the UI had already echoed as sent.
        // No setStatus: the UI stays on the game. If this reopen fails, its own
        // onclose routes through the retry path, which surfaces the visible
        // 'reconnecting'/'disconnected' fallback.
        this.doConnect(this.lastUrl, this.lastSessionId).catch((error) => {
          console.warn('[GameCM] Silent supersede reclaim failed:', error);
        });
        return;
      }

      // Genuine / late / repeat supersede — surface DisconnectedOverlay so the
      // user can manually reclaim. Do NOT auto-reconnect (that would fight the
      // newer connection) and do NOT hard-redirect to /dashboard (4411's job).
      this.stopTokenListener();
      this.messageQueue = [];
      this.reconnectCount = this.config.reconnectAttempts;
      this.setStatus({ status: 'disconnected' });
      return;
    }

    // SIGNED_OUT_ELSEWHERE has its own redirect path (sessionStorage flag +
    // hard navigate to /dashboard). Handled inline rather than via the
    // classifier's generic `kicked` arm because the route differs.
    if (code === WsCloseCodeEnum.SIGNED_OUT_ELSEWHERE) {
      this.sessionTerminated = true;
      this.messageQueue = [];
      this.reconnectCount = this.config.reconnectAttempts;
      // Use the canonical cleanup helper instead of an open-coded list.
      // Hand-rolled removeItem enumerations have repeatedly missed
      // `game-session-ws-url` (load-bearing — `GameLayout` trusts it on
      // mount; `doConnect()` then appends the new sessionId to the stale
      // URL producing `.../ws/<old>/<new>`), `game-session-module`, and
      // `game-storage`. `clearGameSessionState('all')` clears every key in
      // `ROUTE_HINT_KEYS + FULL_RESET_KEYS` and also calls
      // `useGameStore.getState().reset()`, so this branch only needs the
      // SIGNED_OUT_ELSEWHERE-specific bits afterwards: the
      // `session-moved` sessionStorage flag (read by DashboardPage to show
      // the "Session moved" toast) and the hard nav.
      clearGameSessionState('all');
      sessionStorage.setItem('session-moved', 'game');
      window.location.href = '/dashboard';
      reject(new Error('Signed out elsewhere'));
      return;
    }

    const action = classifyCloseCode(code);
    switch (action.kind) {
      case 'auth-recovery': {
        // Per Inv 3, no signOut here. Emit `session-expired` so the global
        // overlay can attempt a silent token refresh; if that fails it
        // surfaces interactive UI. The user keeps their session unless they
        // explicitly click "Sign out".
        //
        // CRITICAL: setStatus('disconnected') is the bridge between silent
        // refresh and reconnection. The token listener only force-reconnects
        // on OPEN sockets, and this socket is closed — without surfacing
        // DisconnectedOverlay, a successful silent refresh would hide the
        // SessionExpiredOverlay and leave the user staring at a stale
        // `connected` UI with a dead socket. DisconnectedOverlay's Reconnect
        // CTA invokes `manualReconnect()`, which already resets
        // `sessionTerminated` and opens a fresh socket with the new token.
        this.sessionTerminated = true;
        this.messageQueue = [];
        this.reconnectCount = this.config.reconnectAttempts;
        clearGameSessionState('route-hints');
        this.setStatus({ status: 'disconnected' });
        emitSessionExpired('game-ws-auth');
        reject(new Error(`Auth failure: ${code}`));
        return;
      }

      case 'terminal-clear-all': {
        // 4003 NOT_PARTICIPANT or 4004 SESSION_NOT_FOUND.
        // `clearIdentity()` also resets `sessionTerminated`, so a fresh
        // startSession in the same tab can proceed.
        this.messageQueue = [];
        this.reconnectCount = this.config.reconnectAttempts;
        clearGameSessionState('all');
        this.clearIdentity();
        this.setStatus({ status: 'ended', reason: 'error' });
        if (code === WsCloseCodeEnum.NOT_PARTICIPANT) {
          toast.error('Not Authorized', {
            description: 'You are not a participant in this game session.',
          });
        } else {
          toast.info('Session Ended', {
            description: 'This game session has ended. Returning to lobby...',
          });
        }
        reject(new Error(`Terminal close ${code}`));
        return;
      }

      case 'terminal-keep-id': {
        // 4100 GAME_ENDED. By the time this fires, `session_ended` has
        // typically already populated the recap UI in gameStore. We MUST
        // NOT reset the store, and we keep `lastSessionId` so a follow-up
        // startSession is correctly identified as a session switch.
        this.sessionTerminated = true;
        this.stopTokenListener();
        this.messageQueue = [];
        this.reconnectCount = this.config.reconnectAttempts;
        // Training duels: keep the game UI up (with Master Kira's recap +
        // coaching) until the player types /end-session. Don't override
        // status — flipping to 'ended' triggers GameLayout's auto-navigate
        // to /lobby, which yanks the player off mid-read.
        //
        // BOTH `sessionEndIsDuel` AND `sessionEndSummary` must be set in the
        // store for us to treat this as the post-recap window. Without the
        // summary check, a network drop mid-LLM-call (4100 arrives before
        // session_ended is delivered) leaves the player on a 'connected' UI
        // with a dead WS and no recap — stuck. With it, the dropped path
        // falls through to the standard 'ended' flip and /lobby navigation.
        //
        // `sessionEndIsDuel` is set from the WS event's `isTrainingDuel` field
        // (in gameStore's session_ending / session_ended handlers). Sourcing
        // it from the store instead of the `game-session-duel-portraits`
        // localStorage hint decouples this terminal logic from the unrelated
        // portrait-loader path that used to clear the flag and break the
        // recap window.
        const { sessionEndSummary, sessionEndIsDuel } = useGameStore.getState();
        const isDuelRecapWindow = sessionEndIsDuel && sessionEndSummary !== null;
        if (!isDuelRecapWindow) {
          clearGameSessionState('route-hints');
        }
        const current = useGameStore.getState().connectionStatus;
        // A 4100 GAME_ENDED close means the session is definitively over: unless
        // we're holding the duel recap window open, the client MUST end up
        // navigating to /lobby. Do NOT additionally gate on `!wasAlreadyTerminated`
        // (#349): `session_ended`'s pre-intercept sets `sessionTerminated=true`
        // BEFORE its store handler runs, so if that handler throws before it can
        // flip status to 'ended' (e.g. a localStorage QuotaExceeded out of any
        // zustand `set()`), `wasAlreadyTerminated` would suppress this backstop
        // and freeze the UI on the dead 'ending' screen. `current.status !==
        // 'ended'` already prevents a redundant double-set on the happy path.
        if (current.status !== 'ended' && !isDuelRecapWindow) {
          this.setStatus({ status: 'ended', reason: 'dm_ended' });
        }
        this.lastUrl = null;
        reject(new Error('Game session ended'));
        return;
      }

      case 'kicked': {
        // 4101 PLAYER_KICKED — distinct from SIGNED_OUT_ELSEWHERE which
        // routed inline above. Player was removed from the game by another
        // player or the server. `clearIdentity()` resets `sessionTerminated`
        // so a fresh adventure in the same tab can proceed.
        this.messageQueue = [];
        this.reconnectCount = this.config.reconnectAttempts;
        clearGameSessionState('all');
        this.clearIdentity();
        this.setStatus({ status: 'ended', reason: 'error' });
        toast.error('Removed from Game', {
          description: 'You were removed from this game session.',
        });
        reject(new Error(`Kicked ${code}`));
        return;
      }

      case 'insufficient-tokens': {
        this.messageQueue = [];
        this.reconnectCount = this.config.reconnectAttempts;
        clearGameSessionState('all');
        this.clearIdentity();
        window.location.href = '/dashboard';
        reject(new Error('Insufficient PM tokens'));
        return;
      }

      case 'retry': {
        // Rapid-cycle escape: socket opened but closed before any server
        // message landed (cold-start saturation, /play first-load racing the
        // grace cleanup, half-open). Count consecutive failures; trip
        // status=disconnected at the limit so the existing DisconnectedOverlay
        // surfaces a manual retry instead of looping silently.
        if (!this.firstMsgAfterOpen) {
          this.handshakeFailures += 1;
          if (this.handshakeFailures >= GameConnectionManager.HANDSHAKE_FAILURE_LIMIT) {
            console.warn(
              `[GameCM] Handshake failed ${this.handshakeFailures}× without messages — surfacing manual reconnect`
            );
            this.autoReconnectDisabled = true;
            this.clearReconnectTimer();
            this.setStatus({ status: 'disconnected' });
            toast.error('Game Connection Failed', {
              description: 'Unable to establish a stable connection.',
              duration: 5000,
            });
            reject(new Error('Repeated handshake failure'));
            return;
          }
        }

        // Server-initiated 1000 carries a slightly different message than
        // the generic transient case, but both retry via the liveness gate.
        if (code === WsCloseCodeEnum.NORMAL_CLOSURE) {
          reject(new Error('Server closed connection (restart?), reconnecting...'));
          toast.warning('Game Connection Lost', {
            description: 'Server restarted. Attempting to reconnect...',
          });
          this.scheduleReconnect();
          return;
        }
        if (this.reconnectCount < this.config.reconnectAttempts) {
          reject(new Error(`Connection lost (code ${code}), reconnecting...`));
          this.gateRetryOnLiveness();
        } else {
          // Retries exhausted — surface overlay, stop auto-reconnect.
          this.autoReconnectDisabled = true;
          this.setStatus({ status: 'disconnected' });
          toast.error('Game Connection Failed', {
            description: 'Unable to reconnect to game session.',
            duration: 5000,
          });
          reject(new Error('Permanent game connection failure'));
        }
        return;
      }
    }
  }

  /**
   * Before scheduling a reconnect on a non-terminal close (typically 1006
   * — half-open / server crash / network), ask the server if the session is
   * still alive. If it's gone, kill the retry chain immediately so the FE
   * doesn't burn ~60s of exponential backoff against a dead session. On any
   * liveness-check error we fail open and retry as before.
   */
  private gateRetryOnLiveness(): void {
    const sessionId = this.lastSessionId;
    // Teardown guard: endSession() preserves lastSessionId and resets
    // sessionTerminated to false, so neither check below detects an
    // endSession() landing during the liveness fetch; only the epoch bump
    // does. Without it, the stale closure schedules a fresh reconnect timer
    // AFTER endSession() already cleared timers, and doConnect captures the
    // post-bump epoch and opens an orphan socket (same class as the
    // _startSession validation window).
    const epochAtGate = this.connectionEpoch;
    if (!sessionId) {
      // No session context — nothing to verify; fall through to retry.
      toast.warning('Game Connection Lost', {
        description: 'Attempting to reconnect to game session...',
      });
      this.scheduleReconnect();
      return;
    }

    isSessionAlive(sessionId)
      .then((alive) => {
        // Identity guard: if the user switched sessions during the 3s liveness
        // fetch, `startSession(newId)` already cleared `sessionTerminated` and
        // overwrote `lastSessionId`. Mutating cleanup state from this stale
        // closure would clobber the new session's connection identity.
        if (this.connectionEpoch !== epochAtGate) return;
        if (this.lastSessionId !== sessionId) return;
        if (this.sessionTerminated) return;
        if (alive) {
          toast.warning('Game Connection Lost', {
            description: 'Attempting to reconnect to game session...',
          });
          this.scheduleReconnect();
          return;
        }
        // Session confirmed dead — short-circuit the retry chain.
        // `clearIdentity()` resets `sessionTerminated` so a fresh adventure
        // in the same tab proceeds (without it the next startSession(newId)
        // takes neither the session-switch reset path nor the terminal
        // bypass and rejects).
        this.stopTokenListener();
        this.clearReconnectTimer();
        this.messageQueue = [];
        this.reconnectCount = this.config.reconnectAttempts;
        clearGameSessionState('all');
        this.clearIdentity();
        this.setStatus({ status: 'ended', reason: 'error' });
        toast.info('Session Ended', {
          description: 'This game session has ended. Returning to lobby...',
        });
      })
      .catch(() => {
        // isSessionAlive is fail-open, so this branch shouldn't fire — but
        // belt-and-suspenders: if it does, retry as today.
        if (this.connectionEpoch !== epochAtGate) return;
        if (this.lastSessionId !== sessionId) return;
        if (this.sessionTerminated) return;
        toast.warning('Game Connection Lost', {
          description: 'Attempting to reconnect to game session...',
        });
        this.scheduleReconnect();
      });
  }

  // ===========================================================================
  // Private — reconnection (C1)
  // ===========================================================================

  private scheduleReconnect(): void {
    if (this.sessionTerminated) return;
    if (this.autoReconnectDisabled) return;
    if (this.reconnectTimer) return;
    if (this.reconnectCount >= this.config.reconnectAttempts) {
      console.warn('[GameCM] Max reconnect attempts reached; giving up');
      this.autoReconnectDisabled = true;
      this.setStatus({ status: 'disconnected' });
      return;
    }

    this.reconnectCount++;
    this.setStatus({ status: 'reconnecting', attempt: this.reconnectCount });

    const delay = Math.min(
      30000,
      this.config.reconnectDelay * Math.pow(2, this.reconnectCount - 1)
    );

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      if (this.lastUrl && this.lastSessionId) {
        this.doConnect(this.lastUrl, this.lastSessionId).catch((error) => {
          console.error('[GameCM] Reconnect failed:', error);
        });
      }
    }, delay);
  }

  // ===========================================================================
  // Private — token listener (G16)
  // ===========================================================================

  private startTokenListener(): void {
    this.stopTokenListener();
    this.tokenUnsubscribe = onIdTokenChanged(auth, async (user) => {
      if (user) {
        try {
          const newToken = await user.getIdToken();
          const tokenChanged = this.currentToken && this.currentToken !== newToken;
          this.currentToken = newToken;
          if (!tokenChanged) return;

          if (this.ws?.readyState === WebSocket.OPEN) {
            // Live socket — close + reopen with new token.
            console.log('[GameCM] Token refreshed, force reconnecting');
            this.forceReconnect();
            return;
          }
          // Closed socket but identity intact and we previously hit
          // auth-recovery (`sessionTerminated=true`). The silent token
          // refresh that just succeeded is the trigger to bring the
          // session back automatically — `manualReconnect()` resets the
          // terminal flag, opens a fresh socket, and the
          // `DisconnectedOverlay` that auth-recovery surfaced disappears
          // on its own once the status flips. Without this branch the
          // user has to click `Reconnect` even though refresh succeeded.
          //
          // Concurrency invariant: the `sessionTerminated` read MUST stay
          // BELOW the `await user.getIdToken()` above and there must be no
          // await between this guard and the `manualReconnect()` dispatch.
          // If `endSession()` lands during the await it sets
          // `sessionTerminated=false` and nulls `ws`; the post-await guard
          // then sees the updated value and correctly skips. Reordering or
          // inserting an await between the guard and dispatch breaks that —
          // the listener could read a stale `true`, yield, and dispatch a
          // reconnect for a session the user already left.
          if (this.lastUrl && this.lastSessionId && this.sessionTerminated) {
            console.log('[GameCM] Token refreshed after auth-recovery; auto-reconnecting');
            this.manualReconnect().catch((error) => {
              console.error('[GameCM] Auto-reconnect after token refresh failed:', error);
            });
          }
        } catch (error) {
          console.error('[GameCM] Failed to get refreshed token:', error);
        }
      } else {
        this.currentToken = null;
        if (this.ws?.readyState === WebSocket.OPEN) {
          console.log('[GameCM] User signed out, ending session');
          this.endSession();
        }
      }
    });
  }

  private stopTokenListener(): void {
    if (this.tokenUnsubscribe) {
      this.tokenUnsubscribe();
      this.tokenUnsubscribe = null;
    }
  }

  private forceReconnect(): void {
    if (!this.lastUrl || !this.lastSessionId) return;
    if (this.ws) {
      // Detach handlers BEFORE closing — prevents stale onclose from triggering
      // scheduleReconnect() after doConnect() clears intentionalClose.
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onmessage = null;
      this.ws.close(WsCloseCodeEnum.NORMAL_CLOSURE, 'Token refresh');
      this.ws = null;
    }
    this.reconnectCount = 0;
    // Reset handshake-failure counter on intentional reconnects too — token
    // refresh races can otherwise trip the manual-retry overlay even though
    // the prior failures were unrelated to the current cycle.
    this.handshakeFailures = 0;
    this.firstMsgAfterOpen = false;
    this.doConnect(this.lastUrl, this.lastSessionId).catch((error) => {
      console.error('[GameCM] Force reconnect failed:', error);
    });
  }

  // ===========================================================================
  // Private — message handling
  // ===========================================================================

  private handleMessage(data: string): void {
    if (!this.firstMsgAfterOpen) {
      this.firstMsgAfterOpen = true;
      this.handshakeFailures = 0;
    }
    try {
      const raw = JSON.parse(data);
      const parsed = serverEventSchema.safeParse(raw);
      if (!parsed.success) {
        console.warn('[GameCM] Dropping invalid server message', parsed.error.issues);
        return;
      }
      const event = parsed.data as ServerMessage;

      // Intercept session_ended BEFORE detailed validation — this is a terminal
      // event that MUST be processed even if the schema has minor mismatches.
      // Without this, a Zod validation failure drops the message, sessionTerminated
      // stays false, and the subsequent onclose triggers infinite reconnect loops.
      if (event.type === 'session_ended') {
        this.sessionTerminated = true;
        this.clearReconnectTimer();
        this.stopTokenListener();
        this.lastUrl = null;
        // Keep lastSessionId so startSession() can detect a session switch and reset sessionTerminated
      }

      // Migration window: server may still emit `{type:"pong"}` to old clients
      // before PR6c lands. Drop silently so it doesn't reach the store.
      if ((event as { type: string }).type === 'pong') return;

      const schema = serverMessageSchemas[event.type as keyof typeof serverMessageSchemas];
      if (schema) {
        const refined = schema.safeParse(raw);
        if (!refined.success) {
          console.warn('[GameCM] Dropping message that failed shape validation', {
            type: event.type,
            issues: refined.error.issues,
          });
          // Still process session_ended even if validation fails — the terminal
          // state was already set above, now let handleGameEvent do its cleanup
          if (event.type !== 'session_ended') {
            return;
          }
        }
      }
      // Drop non-terminal messages after session ended — the store may already
      // be reset, and processing stale round_result/game_state on empty state
      // causes silent failures.
      if (this.sessionTerminated && event.type !== 'session_ended') {
        console.debug('[GameCM] Dropping message after session terminated:', event.type);
        return;
      }
      // Defense-in-depth against scene-transition races: tokio::spawn'd LLM
      // calls can complete after the client advanced to the next scene; their
      // round_result / narrative_preview / narrative_stream arrives stamped
      // with the OLD scene id and would otherwise narrate against the new
      // scene's UI. Backend is the primary defense (filters intents at
      // collect_intents_only); this is the last-mile guard.
      const staleScene = this.shouldFilterBySceneId(event);
      if (staleScene) {
        return;
      }
      useGameStore.getState().handleGameEvent(event);
    } catch (error) {
      console.error('[GameCM] Failed to parse game message:', error);
    }
  }

  /**
   * Returns true iff the event carries a scene_id that doesn't match the
   * client's current scene. Returns false (don't filter) when:
   *   - the event has no scene_id field (non-scene-anchored types), or
   *   - the store hasn't yet seen a scene (initial connect, can't compare).
   */
  private shouldFilterBySceneId(event: ServerMessage): boolean {
    const sceneId =
      'scene_id' in event && typeof event.scene_id === 'string' ? event.scene_id : null;
    if (sceneId === null) return false;
    const currentSceneId = useGameStore.getState().currentSceneId;
    if (currentSceneId === null) return false;
    if (sceneId === currentSceneId) return false;
    console.debug(
      '[GameCM] Dropping stale-scene message:',
      event.type,
      'msg=',
      sceneId,
      'current=',
      currentSceneId
    );
    return true;
  }

  private flushMessageQueue(): void {
    while (this.messageQueue.length > 0) {
      const msg = this.messageQueue.shift();
      if (msg && this.ws?.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify(msg));
      }
    }
  }

  /** Confirm a mounted combat clip reached the browser's playable state. */
  acknowledgeCombatHighlightRendered(id: string): void {
    const acknowledgement: ClientMessage = {
      type: 'combat_highlight_ack',
      id,
      displayed: true,
    };
    if (this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(acknowledgement));
      return;
    }
    this.messageQueue.push(acknowledgement);
    if (this.messageQueue.length > MAX_MESSAGE_QUEUE_SIZE) {
      this.messageQueue.splice(0, this.messageQueue.length - MAX_MESSAGE_QUEUE_SIZE);
    }
  }

  // ===========================================================================
  // Private — helpers
  // ===========================================================================

  private setStatus(status: GameConnectionStatus): void {
    useGameStore.getState().setConnectionStatus(status);
  }

  private async getAuthToken(): Promise<string | null> {
    try {
      const user = auth.currentUser;
      return user ? await user.getIdToken() : null;
    } catch {
      return null;
    }
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private clearStabilityTimer(): void {
    if (this.stabilityTimer) {
      clearTimeout(this.stabilityTimer);
      this.stabilityTimer = null;
    }
  }

  /**
   * Null this manager's session identity AND clear `sessionTerminated`.
   *
   * The reset of `sessionTerminated` is load-bearing: without it, a follow-up
   * `startSession(newId)` in the same tab can't take the session-switch reset
   * path (which requires `lastSessionId !== null && !== newId`) — it falls
   * through to the terminal guard and rejects with "Session was terminated by
   * server", leaving the user unable to start a new adventure without
   * reloading. Bundling identity-nuke and terminal-flag-clear here keeps every
   * caller right by default; arms that need the terminal flag preserved
   * (notably `terminal-keep-id` for 4100/recap) intentionally avoid calling
   * `clearIdentity()` and null `lastUrl` directly instead.
   */
  public clearIdentity(): void {
    this.lastUrl = null;
    this.lastSessionId = null;
    this.sessionTerminated = false;
  }
}

// =============================================================================
// Singleton export (HMR-safe)
// =============================================================================

// Preserve singleton across Vite HMR — prevents duplicate WebSocket connections
// when this module is re-executed during development. Without this, each HMR update
// creates a new GameConnectionManager while the old one's WebSocket stays open,
// causing a rapid connect→disconnect→reconnect loop.
function getOrCreateSingleton(): GameConnectionManager {
  const key = '__gameConnectionManager';
  const global = globalThis as Record<string, unknown>;
  if (!global[key]) {
    global[key] = new GameConnectionManager();
  }
  return global[key] as GameConnectionManager;
}

export const gameConnectionManager = getOrCreateSingleton();
export default gameConnectionManager;
